import { useMemo } from 'react';
import { fromZonedTime } from 'date-fns-tz';

// Shared constants - prevents duplication
export const MIN_JOB_MINUTES = 4 * 60;
export const BREAK_DISABLED_BELOW_MINUTES = 6 * 60;
export const SIXTY_MIN_BREAK_AT_MINUTES = 8 * 60;

// Helper: convert 12h time to 24h (HH:MM:SS)
const time12To24 = (t: string) => {
  const m = t.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!m) return '00:00:00';
  let h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(mn)}:00`;
};

// Helper: convert time label to minutes
const labelToMinutes = (t: string) => {
  const m = t.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (!m) return 0;
  let h = parseInt(m[1], 10);
  const mn = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return h * 60 + mn;
};

/**
 * Calculate wall-clock duration (ignores DST, for validation only)
 * Example: 11 PM to 3 AM = 4 hours (always)
 */
export const calculateWallClockDurationMinutes = (
  startLabel: string,
  endLabel: string,
): number => {
  let diff = labelToMinutes(endLabel) - labelToMinutes(startLabel);
  if (diff <= 0) diff += 24 * 60;
  return diff;
};

/**
 * Calculate timezone-aware UTC duration (accounts for DST)
 * Example: 11 PM to 3 AM EST on DST day = 3 hours UTC
 */
export const calculateUtcDurationMinutes = (
  startLabel: string,
  endLabel: string,
  dateKey: string,
  timezone: string,
): number => {
  const startMins = labelToMinutes(startLabel);
  const endMins = labelToMinutes(endLabel);
  const isOvernight = endMins <= startMins;

  let endDateKey = dateKey;
  if (isOvernight) {
    const [y, m, d] = dateKey.split('-').map(Number);
    const utcDate = new Date(Date.UTC(y, m - 1, d + 1));
    endDateKey = utcDate.toISOString().slice(0, 10);
  }

  const startWall = `${dateKey}T${time12To24(startLabel)}`;
  const endWall = `${endDateKey}T${time12To24(endLabel)}`;

  const startUtc = fromZonedTime(startWall, timezone);
  const endUtc = fromZonedTime(endWall, timezone);

  const diffMs = endUtc.getTime() - startUtc.getTime();
  return Math.round(diffMs / 1000 / 60);
};

/**
 * Single hook for all duration calculations and validations
 * Returns wall-clock (validation) and UTC (display) durations with flags
 */
export const useDurationCalculations = (
  startLabel: string,
  endLabel: string,
  dateKey: string | null,
  timezone: string,
) => {
  // Wall-clock duration (for validation, ignores DST)
  const wallClockMins = useMemo(
    () => calculateWallClockDurationMinutes(startLabel, endLabel),
    [startLabel, endLabel],
  );

  // UTC duration (for display/totals, accounts for DST)
  const utcMins = useMemo(() => {
    if (!dateKey) return 0;
    return calculateUtcDurationMinutes(startLabel, endLabel, dateKey, timezone);
  }, [startLabel, endLabel, dateKey, timezone]);

  // Validation flags (use wall-clock for user expectations)
  const durationTooShort = wallClockMins < MIN_JOB_MINUTES;
  const breakDisabled = wallClockMins < BREAK_DISABLED_BELOW_MINUTES;
  const breakOptions =
    wallClockMins >= SIXTY_MIN_BREAK_AT_MINUTES
      ? ['0 min', '15 mins', '30 mins', '45 mins', '60 mins']
      : ['0 min', '15 mins', '30 mins', '45 mins'];

  // Format display string
  const displayHours = (utcMins / 60).toFixed(2);
  const displayString = `${parseFloat(displayHours)} hrs`;

  return {
    wallClockMins, // For validation (>= 4 hours check)
    utcMins, // For display/totals (actual duration)
    durationTooShort, // Validation flag
    breakDisabled, // Break logic flag
    breakOptions, // Available break options
    displayString, // Formatted "X.XX hrs"
  };
};

export default useDurationCalculations;
